import { Fragment, useEffect, useState } from 'react'
import type { User } from '../../../utils/loginUtils'
import { getRecipeList, type recipe } from '../../../utils/recipeUtils'
import RecipeEntry from './RecipeEntry'
import Popup, { type ExtraButton } from '../../PopupForm/Popup'
import RecipePopUpContent from './RecipePopUpContent'
import Spinner from '../../Spinner/Spinner'
import { getMessage } from '../../../utils/messageUtils'

import './RecipeList.scss'
import {
  getIngredientList,
  type ingredient,
} from '../../../utils/ingredientUtils'
import ListHeader from '../ListHeader'
import FilterBar, { type filter } from '../FilterBar'
import type { Mode } from '../../../App'
import type { MealType } from '../../../utils/calendarUtils'
interface RecipeListProps {
  user: User
  isMobile: boolean
  setMode: (mode: Mode) => void
}

function RecipeList({ user, isMobile, setMode }: RecipeListProps) {
  const [loadData, setLoadData] = useState<boolean>(true)
  const [recipeList, setRecipeList] = useState<recipe[] | undefined>(undefined)

  // Filters
  const [mealTypes, setMealTypes] = useState<MealType[]>([])
  const [mealTypesForPopup, setMealTypesForPopup] = useState<MealType[]>([])
  const [nameFilter, setNameFilter] = useState<string>('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [desayunoFilter, setDesayunoFilter] = useState<boolean>(true)
  const [almuerzoFilter, setAlmuerzoFilter] = useState<boolean>(true)
  const [comidaFilter, setComidaFilter] = useState<boolean>(true)
  const [meriendaFilter, setMeriendaFilter] = useState<boolean>(true)
  const [cenaFilter, setCenaFilter] = useState<boolean>(true)

  // Popup
  const [popupOpen, setPopupOpen] = useState<boolean>(false)
  const [popupMessage, setPopupMessage] = useState<string>('')
  const [popupChildren, setPopupChildren] = useState<React.ReactNode>(null)
  const [popupOnOk, setPopupOnOk] = useState<(() => void) | undefined>(
    undefined
  )
  const [popupExtraButtons, setPopupExtraButtons] = useState<
    ExtraButton[] | undefined
  >(undefined)

  const [existingIngredients, setExistingIngredients] = useState<ingredient[]>(
    []
  )

  const [isLoading, setIsLoading] = useState(false)

  const otherMessage = getMessage('others')

  useEffect(() => {
    const generateTypes = (recipes: recipe[] | undefined) => {
      if (!recipes) return
      const types = new Set<string>()
      recipes.forEach(recipe => {
        if (recipe.type) {
          types.add(recipe.type)
        } else {
          types.add(otherMessage)
        }
      })
      setMealTypes(Array.from(types) as MealType[])
      setMealTypesForPopup(
        Array.from(types).filter(type => type !== otherMessage) as MealType[]
      )
    }

    const fetchRecipeList = async () => {
      setLoadData(false)
      setIsLoading(true)
      try {
        const data = await getRecipeList(user.id!)
        if (data.success) {
          generateTypes(data.recipes)
          setRecipeList(data.recipes)
        }
        if (!existingIngredients || existingIngredients.length === 0) {
          const ingredientData = await getIngredientList(user.id!)
          if (ingredientData.success && ingredientData.ingredients) {
            setExistingIngredients(ingredientData.ingredients)
          }
        }
      } finally {
        setIsLoading(false)
      }
    }

    if (loadData) {
      fetchRecipeList()
    }
  }, [loadData, user.id, otherMessage, existingIngredients])

  const applyFilters = (recipe: recipe) => {
    return (
      (nameFilter === '' ||
        recipe.name.toLowerCase().includes(nameFilter.toLowerCase())) &&
      (typeFilter === '' ||
        recipe.type === typeFilter ||
        (typeFilter === otherMessage && !recipe.type)) &&
      ((desayunoFilter && recipe.desayuno) ||
        (almuerzoFilter && recipe.almuerzo) ||
        (comidaFilter && recipe.comida) ||
        (meriendaFilter && recipe.merienda) ||
        (cenaFilter && recipe.cena))
    )
  }

  const generateRecipeList = () => {
    let previousType: string | null = ''
    return recipeList
      ?.filter(recipe => applyFilters(recipe))
      .map(recipe => {
        if (recipe.type !== previousType) {
          previousType = recipe.type
          return (
            <Fragment>
              <ListHeader
                title={previousType ? previousType : otherMessage}
                isMobile={isMobile}
              />
              <RecipeEntry
                key={recipe.name}
                recipe={recipe}
                isMobile={isMobile}
                openPopup={openPopup}
              />
            </Fragment>
          )
        }
        return (
          <RecipeEntry
            key={recipe.name}
            recipe={recipe}
            isMobile={isMobile}
            openPopup={openPopup}
          />
        )
      })
  }

  const openPopup = (obj: object | null) => {
    const recipeToEdit = obj as recipe | null
    const title = recipeToEdit ? recipeToEdit.name : getMessage('recipe_new')
    setPopupMessage(title)
    setPopupChildren(
      <RecipePopUpContent
        recipe={recipeToEdit}
        currentTypes={mealTypesForPopup}
        recipeList={recipeList}
        user={user}
        existingIngredients={existingIngredients}
        isMobile={isMobile}
        setPopupOnOk={setPopupOnOk}
        closePopup={() => setPopupOpen(false)}
        reLoadData={() => setLoadData(true)}
        setPopupExtraButtons={setPopupExtraButtons}
        setMode={setMode}
      />
    )
    setPopupOpen(true)
  }

  const generateFilterItems = () => {
    return [
      {
        type: 'text',
        placeholder: getMessage('recipe'),
        value: nameFilter,
        setValue: setNameFilter,
      },
      {
        label: getMessage('type'),
        type: 'select',
        options: mealTypes,
        value: typeFilter,
        setValue: setTypeFilter,
      },
      {
        type: 'checkbox',
        checkboxes: [
          {
            label: getMessage('breakfast'),
            value: desayunoFilter,
            setValue: setDesayunoFilter,
          },
          {
            label: getMessage('almuerzo'),
            value: almuerzoFilter,
            setValue: setAlmuerzoFilter,
          },
          {
            label: getMessage('lunch'),
            value: comidaFilter,
            setValue: setComidaFilter,
          },
          {
            label: getMessage('merienda'),
            value: meriendaFilter,
            setValue: setMeriendaFilter,
          },
          {
            label: getMessage('dinner'),
            value: cenaFilter,
            setValue: setCenaFilter,
          },
        ],
      },
    ] as filter[]
  }

  return (
    <div
      className={`recipe-list-container ${isMobile ? 'recipe-list-container-mobile' : ''}`}
    >
      <FilterBar
        filters={generateFilterItems()}
        openPopup={openPopup}
        isMobile={isMobile}
      />
      <div className="recipe-list-body-container">{generateRecipeList()}</div>
      <Popup
        isOpen={popupOpen}
        message={popupMessage}
        children={popupChildren}
        isMobile={isMobile}
        extraButtons={popupExtraButtons}
        onOk={popupOnOk}
        onClose={() => setPopupOpen(false)}
      />
      <Spinner isOpen={isLoading} overlay={true} />
    </div>
  )
}

export default RecipeList
